import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, signal, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ReplaySubject, takeUntil, firstValueFrom } from 'rxjs';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-modal';
import { ToastrService } from '../../services/toastr-service';
import { ShopManagementService } from '../../services/shop-management.service';
import { MasterDataManagementService } from '../../services/master-data-management.service';
import { CreateShopRequest } from '../../models/create-shop-request.model';
import { ShopResponse } from '../../models/shop-response.model';
import { ShopDetailView } from '../../models/shop-detail-view.model';
import { ErrorComponent } from '../../shared-components/error/error.component';
import { CreateShopsShopInformationComponent } from '../../components/create-shops/create-shops-shop-information/create-shops-shop-information.component';
import { EditShopsShopInformationComponent } from '../../components/edit-shops/shop-information/edit-shops-shop-information.component';
import { CreateShopsFinancialConfigurationComponent } from '../../components/create-shops/create-shops-financial-configuration/create-shops-financial-configuration.component';
import { CreateShopsOperationalSettingsComponent } from '../../components/create-shops/create-shops-operational-settings/create-shops-operational-settings.component';
import { ShopFormValue } from '../../form-model/create-shop-header.form.model';
import { MasterDataAuditLogComponent } from '../../shared-components/master-data-audit-log/master-data-audit-log.component';
import { AuthPolicies } from '../../auth/auth-policies';

@Component({
  selector: 'app-create-shops',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    CreateShopsShopInformationComponent,
    EditShopsShopInformationComponent,
    CreateShopsFinancialConfigurationComponent,
    ErrorComponent,
    NgxSkeletonLoaderModule,
    CreateShopsOperationalSettingsComponent,
    MasterDataAuditLogComponent,
  ],
  templateUrl: './create-shops.component.html',
  styleUrls: ['./create-shops.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CreateShopsComponent implements OnInit, OnDestroy {
  formSubmitErrors = signal<string[]>([]);
  submitLoader = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  showCancelDialog = signal<boolean>(false);
  shopCode: string | null = null;
  shopData = signal<ShopDetailView | null>(null);
  openAuditLog: boolean = false;

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  createShopForm = this.formBuilder.group({});

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastrService: ToastrService,
    private readonly shopManagementService: ShopManagementService,
    private readonly masterDataManagementService: MasterDataManagementService,
    public authPolicy: AuthPolicies,
  ) {}

  ngOnInit(): void {
    // Check if we're in edit mode by looking for shopCode in route params
    this.shopCode = this.route.snapshot.paramMap.get('shopCode');

    // Always initialize form controls first
    this.addControlsToTheFormGroup();

    if (this.shopCode) {
      this.isEditMode.set(true);
      this.loadShopData(this.shopCode)
        .then(() => {})
        .catch(() => {});
    } else {
      this.isEditMode.set(false);
    }
  }

  private async loadShopData(shopCode: string): Promise<void> {
    try {
      const data = await firstValueFrom(this.masterDataManagementService.getShopDetailByCode(shopCode));
      this.shopData.set(data);
      this.populateFormWithData(data);
    } catch (err: any) {
      console.error('Error loading shop data:', err);
      this.toastrService.showToastr('Failed to load shop data', 'error');
      this.router.navigate(['/master-data/shop-management']);
    }
  }

  private populateFormWithData(data: ShopDetailView | null): void {
    if (!data) return;

    this.createShopForm.patchValue({
      shopCode: data.shopCode,
      shopType: data.partSupplierType,
      description: data.name,
      emailAddress: data.emailAddress || '',
      phoneNumber: data.phone || '',
      shopLocation: data.locationCode,
      currency: data.currencyCode,
      linkToAccounting: data.linkToAccounting || '',
      shopActive: data.isActive,
      decentralised: data.isDecentralized,
      preparationTime: data.isPrepTimeEnabled,
      mode49Flag: data.isMode49Flag,
    });

    // Mark form as pristine after initial load to track actual user changes
    this.createShopForm.markAsPristine();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private addControlsToTheFormGroup(): void {
    this.createShopForm.addControl(
      'shopCode',
      this.formBuilder.nonNullable.control<string>('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(3),
        Validators.pattern(/^[a-zA-Z0-9]{3}$/),
      ]),
    );
    this.createShopForm.addControl('shopType', this.formBuilder.nonNullable.control<string>('', Validators.required));
    this.createShopForm.addControl(
      'description',
      this.formBuilder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(200)]),
    );
    this.createShopForm.addControl('emailAddress', this.formBuilder.control<string>('', [Validators.email, Validators.maxLength(60)]));
    this.createShopForm.addControl('phoneNumber', this.formBuilder.control<string>('', [Validators.maxLength(20)]));
    this.createShopForm.addControl(
      'shopLocation',
      this.formBuilder.nonNullable.control<string>('', [Validators.required, Validators.minLength(5), Validators.maxLength(8)]),
    );

    this.createShopForm.addControl(
      'currency',
      this.formBuilder.nonNullable.control<string>('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(3),
        Validators.pattern(/^[a-zA-Z]{3}$/),
      ]),
    );

    this.createShopForm.addControl('linkToAccounting', this.formBuilder.control<string>(''));

    this.createShopForm.addControl('shopActive', this.formBuilder.control<boolean>(false));
    this.createShopForm.addControl('decentralised', this.formBuilder.control<boolean>(false));
    this.createShopForm.addControl('preparationTime', this.formBuilder.control<boolean>(false));
    this.createShopForm.addControl('mode49Flag', this.formBuilder.control<boolean>(false));
  }

  isControlInErrorState(control: FormControl): boolean | null {
    return control?.invalid && control?.touched ? true : null;
  }

  onCancel(): void {
    if (this.createShopForm.dirty) {
      this.showCancelDialog.set(true);
    } else {
      this.navigateToShopManagement();
    }
  }

  confirmCancel(): void {
    this.showCancelDialog.set(false);
    this.navigateToShopManagement();
  }

  keepEditing(): void {
    this.showCancelDialog.set(false);
  }

  private navigateToShopManagement(): void {
    if (this.isEditMode() && this.shopCode) {
      this.router.navigate(['/master-data/shop-management', this.shopCode]);
    } else {
      this.router.navigate(['/master-data/shop-management']);
    }
  }

  onSubmit(): void {
    if (this.createShopForm.invalid) {
      for (const key of Object.keys(this.createShopForm.controls)) {
        this.createShopForm.get(key)?.markAsTouched();
      }
      this.toastrService.showToastr('Please fill in all required fields correctly', 'warning');
      return;
    }

    if (this.isEditMode()) {
      this.updateShop();
    } else {
      this.createShop();
    }
  }

  private buildShopRequest(): CreateShopRequest {
    const formValue = (this.isEditMode() ? this.createShopForm.getRawValue() : this.createShopForm.value) as ShopFormValue;
    return {
      shopCode: formValue.shopCode,
      partSupplierType: formValue.shopType,
      name: formValue.description,
      locationCode: formValue.shopLocation.toUpperCase(),
      emailAddress: formValue.emailAddress || null,
      phone: formValue.phoneNumber || null,
      currencyCode: formValue.currency.toUpperCase(),
      //vendorCode: formValue.vendorCode.toUpperCase(),
      linkToAccounting: formValue.linkToAccounting || null,
      isActive: formValue.shopActive ?? false,
      isMode49Flag: formValue.mode49Flag ?? false,
      isDecentralized: formValue.decentralised ?? false,
      isPrepTimeEnabled: formValue.preparationTime ?? false,
    };
  }

  private createShop(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);

    this.shopManagementService
      .createShop(this.buildShopRequest())
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: ShopResponse) => {
          this.submitLoader.set(false);
          this.createShopForm.reset();
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr(`Shop '${response.shopCode}' created successfully`, 'success');
          this.router.navigate(['/master-data/shop-management']);
        },
        error: (error: HttpErrorResponse) => {
          this.handleShopError(error, 'creating');
        },
      });
  }

  private updateShop(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);

    this.shopManagementService
      .updateShop(this.shopCode!, this.buildShopRequest())
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: ShopResponse) => {
          this.submitLoader.set(false);
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr(`Shop '${response.shopCode}' updated successfully`, 'success');
          this.router.navigate(['/master-data/shop-management', response.shopCode]);
        },
        error: (error: HttpErrorResponse) => {
          this.handleShopError(error, 'updating');
        },
      });
  }

  private handleShopError(error: HttpErrorResponse, operation: string): void {
    this.submitLoader.set(false);
    console.error(`Error ${operation} shop:`, error);

    if (error.status === 400) {
      this.formSubmitErrors.set(['Invalid shop data. Please check your inputs.']);
      this.toastrService.showToastr('Invalid shop data. Please check your inputs.', 'error');
    } else if (error.status === 409) {
      this.formSubmitErrors.set(['Shop code already exists. Please use a different code.']);
      this.toastrService.showToastr('Shop code already exists.', 'error');
    } else if (error.status === 422 && error.error?.violations) {
      const errors = error.error.violations.map((v: { message: string }) => v.message);
      this.formSubmitErrors.set(errors);
      this.toastrService.showToastr('Validation errors occurred. Please check the form.', 'error');
    } else {
      this.formSubmitErrors.set([`Failed to ${operation} shop. Please try again.`]);
      this.toastrService.showToastr(`Failed to ${operation} shop. Please try again.`, 'error');
    }
  }

  onAuditLogClicked() {
    if (!this.shopCode) {
      this.toastrService.showToastr('Shop code not available for audit log', 'error');
      return;
    }
    this.openAuditLog = true;
  }

  onAuditLogClosed() {
    this.openAuditLog = false;
  }

  readonly auditLogFetcher = this.shopManagementService.getShopAuditLogs.bind(this.shopManagementService);
}
